import _ from 'underscore';

import {PastedCell} from 'notebook/tables/constants/CellConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import paste from 'notebook/tables/excess/ops/paste';
import {TableOpDiff} from 'notebook/tables/excess/ops/TableOpConstants';
import tokenizer from 'notebook/tables/excess/Tokenizer';
import * as util from 'notebook/tables/excess/Util';
import {
  formatExcessNumericTypedValue,
  getScientificNotation,
  isBaseNumericValueType,
  isBaseNumericValueTypeArray,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, isTextEditableType} from 'notebook/tables/typed-values/TypedValueConstants';
import prettyFloat from 'util/prettyFloat';

import {EvaluatedCell} from '../Evaluator';

interface CellInfo {
  prefix: string;
  lengthWithPadding: number;
}

/**
 * Helper method that returns a function that extrapolates the value from a list of cells.
 * @param  {Array} cells List of cells having `formula` be a relative formula
 * @param  {Array} evaluatedCells Evaluated cells from the computed table, one per cell.
 *                 evaluatedCells[i].value takes precedence over cell.typedValue for the
 *                 entity-chip check so that non-text-editable types (e.g. BIOENTITY) are tiled
 *                 rather than extrapolated even when the raw cell has no typedValue set.
 * @return {Function}    Given distance from the first cell, returns a new cell with an extrapolated value
 */
function _extrapolate1D(
  cells: Array<PastedCell>,
  evaluatedCells: Array<EvaluatedCell | null | undefined>
): (distFromCell: number) => PastedCell {
  // If there is a common prefix across all cells suffixed with numbers, we still attempt fill extrapolation
  let diff: number | null;
  let lengthWithPadding: number;
  let prefix: string;
  let targetDiff: number;
  let targetPrecision: number | null | undefined;
  const infos: Array<CellInfo | null> = cells.map((cell, index) => {
    if (cell.formula == null) {
      return null;
    }
    if (util.getFormulaText(cell.formula) != null) {
      return null;
    }
    // If the value is already numeric there is no prefix, unless it has leading zeroes
    if (
      isBaseNumericValueType(util.parseValue(cells[0].formula!)) &&
      // If the number has any leading zeroes, treat it like a string for fill down and make sure following
      // numbers have the same number of leading zeroes.
      !util.NUMBER_HAS_LEADING_ZEROES_REGEX.exec(cells[0].formula!)
    ) {
      return null;
    }
    const match = /^(.*?)(\d+)$/.exec(cell.formula);
    if (match == null) {
      return null;
    }
    if (cell.link) {
      return null;
    }
    // If a cell has an entity typedValue (e.g. a bioentity chip), do not extrapolate — just tile.
    // Text-editable types (string, number, etc.) can be safely extrapolated by formula text.
    // Prefer the evaluated typed value from the computed table when available, since raw cells
    // often omit typedValue even when the cell contains an entity chip.
    const effectiveTypedValue = evaluatedCells[index]?.value ?? cell.typedValue;
    if (
      effectiveTypedValue != null &&
      !isTextEditableType(effectiveTypedValue) &&
      effectiveTypedValue.type !== BaseTypedValueType.GRID_COORDINATES
    ) {
      return null;
    }
    return {prefix: match[1], lengthWithPadding: match[2].startsWith('0') ? match[2].length : 0};
  });

  if (infos.length > 0 && infos[0] && infos.every((info) => info && info.prefix === infos[0]!.prefix)) {
    ({prefix} = infos[0]);
    lengthWithPadding = Math.max(...Array.from(infos.map((info) => info!.lengthWithPadding) || []));
  } else {
    prefix = '';
    lengthWithPadding = 0;
  }

  // Find the numeric values in the non-fixed dimension.
  const values = cells.map((cell) =>
    cell.formula ? util.parseValue(cell.formula.slice(prefix.length)) : null
  );
  diff = null;
  targetPrecision = null;
  if (values.length && isBaseNumericValueTypeArray(values)) {
    if (values.length === 1) {
      if (prefix) {
        diff = 1;
        targetPrecision = values[0].defaultPrecision;
      }
    } else {
      const precisions = values.flatMap((value) =>
        value.defaultPrecision != null ? value.defaultPrecision : []
      );
      targetPrecision = precisions.length ? Math.max(...precisions) : null;
      diff = parseFloat(prettyFloat(values[1].rawValue - values[0].rawValue, targetPrecision));
      // If any of the subsequent pairs do not match this difference (within EPSILON to handle floating point),
      // then the values are not linear.
      for (let i = 1, end = values.length, asc = 1 <= end; asc ? i < end : i > end; asc ? i++ : i--) {
        const newDiff = parseFloat(prettyFloat(values[i].rawValue - values[i - 1].rawValue, targetPrecision));
        if (newDiff !== diff) {
          diff = null;
          break;
        }
      }
    }
  }

  if (diff == null) {
    // If the cells are not linear, we simply tile the values.
    // e.g. [1, 'a', 3, 4] is extended by [1, 'a', 3, 4]
    targetDiff = 0;
    targetPrecision = null;
  } else {
    // Otherwise, we increment by the number of values,
    // e.g. [1, 2, 3] is extended by [1+3, 2+3, 3+3]
    targetDiff = diff * values.length;
  }

  return function (distFromSrcMin: number): PastedCell {
    const srcIdx = __mod__(distFromSrcMin, cells.length); // Index of cell to extrapolate from.
    const tileNum = Math.floor(distFromSrcMin / cells.length); // Number of tiles away the dst cell is.
    const cell = cells[srcIdx];
    const srcValue = values[srcIdx];

    // If the source cell is not a number, then re-use the cell
    if (srcValue == null || !isBaseNumericValueType(srcValue) || tileNum === 0) {
      return cell;

      // Otherwise, copy the cell but linearly extend the source value
    } else {
      const numWithPadding = `${formatExcessNumericTypedValue(
        {
          ...srcValue,
          rawValue: tileNum * targetDiff + srcValue.rawValue,
        },
        targetPrecision,
        getScientificNotation(cell)
      )}`.padStart(lengthWithPadding, '0');
      const value = `${prefix}${numWithPadding}`;

      return {
        ...cell,
        text: String(value),
        formula: String(value),
        // only null the typed value if it's already set, since many tests do not include one for cells
        ...(cell.typedValue != null ? {typedValue: null} : {}),
      };
    }
  };
}

/**
 * Helper method to compute fill down values in source table into destination table.
 * @param  {Number} srcFixedSize    The fixed dimension of the table (not to be resized)
 * @param  {Number} srcSize         The non-fixed dimension of the table (to be resized)
 * @param  {Number} dstSrcOffset    The (non-fixed-dim) offset between source and destination tables
 * @param  {Function} getSrcCell    Given (fixed-dim index, non-fixed-dim index), returns a cell into
 *                                  into source table.
 * @param  {Function} getEvaluatedCell Given (fixed-dim index, non-fixed-dim index), returns the
 *                                  evaluated cell from the computed table.
 * @return {Function}               Given (fixed-dim index, non-fixed-dim index), returns the cell to be
 *                                  used in the destination table.
 */
function extrapolate2D(
  srcFixedSize: number,
  srcSize: number,
  dstSrcOffset: number,
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  getSrcCell: (fixedDimensionIndex: number, nonFixedDimensionIndex: number) => any,
  getEvaluatedCell: (
    fixedDimensionIndex: number,
    nonFixedDimensionIndex: number
  ) => EvaluatedCell | null | undefined
): (dstFixedIdx: number, dstIdx: number) => PastedCell {
  // For each fixed-dim, we compute a function that computes the extrapolated value in the non-fixed
  // dimension when extrapolating by `distFromSrcMin` cells.
  const cellFns = (() => {
    const result = [];
    for (
      let srcFixedIdx = 0, end = srcFixedSize, asc = 0 <= end;
      asc ? srcFixedIdx < end : srcFixedIdx > end;
      asc ? srcFixedIdx++ : srcFixedIdx--
    ) {
      const cells: Array<PastedCell> = __range__(0, srcSize, false).map((i) => getSrcCell(srcFixedIdx, i));
      const evaluatedCells = __range__(0, srcSize, false).map((i) => getEvaluatedCell(srcFixedIdx, i));
      result.push(_extrapolate1D(cells, evaluatedCells));
    }
    return result;
  })();
  return (dstFixedIdx: number, dstIdx: number) => cellFns[dstFixedIdx](dstIdx + dstSrcOffset);
}

/**
 * Applies fill-down on `table` to region denoted by `dstBox`, extrapolating from the region denoted by
 * `srcBox`. `dstBox` and `srcBox` should match in size in at least one dimension.
 * @param  {Object} table         The table to modify. Should match TableConstants.table.
  * @param  {Array} computedTable 2D array containing the evaluated cells of the table,
                                  containing the results of formulas.
 * @param  {Object} srcBox        An object containing minI, maxI, minJ, maxJ corresponding to the region of the
 *                                table to be tiled.
 * @param  {Object} dstBox        An object containing minI, maxI, minJ, maxJ corresponding to the region of the
 *                                table to be filled-down.
 * @return {Object}               The result of the fill-down, consisting of:
 *                                  table: new table
 *                                  box: the region that was filled into as {minI, maxI, minJ, maxJ}
 */
function fillDown(
  table: Table,
  computedTable: Array<Array<EvaluatedCell>>,
  srcBox: MultiSelectionBox,
  dstBox: MultiSelectionBox | null | undefined,
  disableFormulas: boolean
): {table: Table; box: MultiSelectionBox; tableOp: TableOpDiff} {
  // Source cells: region of table to be tiled for the fill down.
  let cellFn: (dstFixedIdx: number, dstIdx: number) => PastedCell;
  let getValue: (dstI: number, dstJ: number) => PastedCell;
  let dstI;
  const srcCells = __range__(srcBox.minI, srcBox.maxI + 1, false).map((i) =>
    __range__(srcBox.minJ, srcBox.maxJ + 1, false).map((j) =>
      _.chain({})
        .extend(
          table.table[i][j],
          !disableFormulas
            ? {
                formula: tokenizer.toRelativeFormula(table.table[i][j].text, {row: i, column: j}),
              }
            : {}
        )
        // @ts-expect-error
        .omit(!disableFormulas ? 'text' : null)
        .value()
    )
  );
  const srcHeight = srcBox.maxI - srcBox.minI + 1;
  const srcWidth = srcBox.maxJ - srcBox.minJ + 1;

  // Destination cells: the filled-down region of the table to be pasted into the original table.
  const dstHeight = dstBox!.maxI - dstBox!.minI + 1;
  const dstWidth = dstBox!.maxJ - dstBox!.minJ + 1;

  // Extrapolate srcCells to fill dstWidth x dstHeight dstCells.
  if (srcWidth === dstWidth) {
    // When srcWidth == dstWidth, width is the fixed dimension, height is the non-fixed dimension.
    cellFn = extrapolate2D(
      srcWidth,
      srcHeight,
      dstBox!.minI - srcBox.minI,
      (srcJ: number, srcI: number) => srcCells[srcI][srcJ],
      (srcJ: number, srcI: number) => computedTable[srcBox.minI + srcI][srcBox.minJ + srcJ]
    );
    getValue = (dstI: number, dstJ: number) => cellFn(dstJ, dstI);
  } else {
    // Otherwise, height is the fixed dimension, width is the non-fixed dimension.
    cellFn = extrapolate2D(
      srcHeight,
      srcWidth,
      dstBox!.minJ - srcBox.minJ,
      (srcI: number, srcJ: number) => srcCells[srcI][srcJ],
      (srcI: number, srcJ: number) => computedTable[srcBox.minI + srcI][srcBox.minJ + srcJ]
    );
    getValue = (dstI: number, dstJ: number) => cellFn(dstI, dstJ);
  }
  const dstCells = (() => {
    let asc;
    let end;
    const result = [];
    // eslint-disable-next-line benchlint/disallow-comma-operator
    for (dstI = 0, end = dstHeight, asc = 0 <= end; asc ? dstI < end : dstI > end; asc ? dstI++ : dstI--) {
      result.push(
        (() => {
          const result1 = [];
          for (
            let dstJ = 0, end1 = dstWidth, asc1 = 0 <= end1;
            asc1 ? dstJ < end1 : dstJ > end1;
            asc1 ? dstJ++ : dstJ--
          ) {
            result1.push(getValue(dstI, dstJ));
          }
          return result1;
        })()
      );
    }
    return result;
  })();

  return paste(table, computedTable, dstCells, dstBox!.minI, dstBox!.minJ);
}

export default {fillDown, _extrapolate1D};

function __mod__(a: number, b: number): number {
  a = Number(a);
  b = Number(b);
  return ((a % b) + b) % b;
}

function __range__(left: number, right: number, inclusive: boolean): Array<number> {
  const range = [];
  const ascending = left < right;
  const end = !inclusive ? right : ascending ? right + 1 : right - 1;
  for (let i = left; ascending ? i < end : i > end; ascending ? i++ : i--) {
    range.push(i);
  }
  return range;
}
